import React, {useMemo} from "react";
import { X, Minus, MessageSquare, Maximize2, Minimize2 } from "lucide-react";
import chatIcon from "../design-icons/chat-bubble.svg";
import { useChat } from "../store/ChatContext";

// Trigger Button 
export const AIChatTrigger = ({ className = "" }) => {
  const { openChat, chatbotEnabled, configLoading } = useChat()

  if(configLoading || !chatbotEnabled) return null

  return (
    <button
      onClick={openChat}
      className={`flex items-center justify-center w-full rounded-lg py-2 text-zinc-600 hover:bg-gray-100 transition-colors ${className}`}
      aria-label="AI Assistant"
    >
      <img src={chatIcon} alt="AI Assistant" className="w-5 h-5" />
    </button>
  )
};

// Floating Panel 
export const AIChatPanel = ({ user }) => {

  const {
    chatOpen,
    chatMinimized, 
    chatExpanded,
    closeChat, 
    toggleMinimize, 
    toggleExpand,
    chatbotBaseUrl, 
    chatbotAssistant, 
    chatbotEnabled,
  } = useChat();

  const iframeUrl = useMemo(() => {
    if(!chatbotBaseUrl) return null

    const url = new URL(chatbotBaseUrl, window.location.origin);
    url.searchParams.set("assistant", chatbotAssistant || "hiresmart360");
    url.searchParams.set("userName", user?.name || "");
    url.searchParams.set("email", user?.username || "");
    return url.toString();
  }, [user, chatbotBaseUrl, chatbotAssistant]);

  if (!chatOpen) return null;

  return (
    <>
      <div
        style={{
          position: "fixed",
          bottom: 24,
          right: 24,
          width: chatExpanded ? 620 : 420,
          height: chatExpanded ? "calc(100vh - 60px)" : 580,
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10), 0 0 0 1px rgba(0,0,0,0.06)",
          background: "#fff",
          transition: "width 0.25s cubic-bezier(.4,0,.2,1), height 0.25s cubic-bezier(.4,0,.2,1)",
          animation: "floatIn 0.22s cubic-bezier(.4,0,.2,1)",
          opacity: chatMinimized ? 0 : 1,
          pointerEvents: chatMinimized ? "none" : "auto",
          visibility: chatMinimized ? "hidden" : "visible",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 shrink-0 select-none"
          style={{
            height: 52,
            background: "linear-gradient(135deg, #0f766e 0%, #0d9488 60%, #14b8a6 100%)",
          }}
        >
          {/* Left - avatar + title */}
          <div>
            <p className="text-sm font-semibold leading-tight text-white">Aivi</p>
            <p className="text-xs leading-none" style={{ color: "rgba(255,255,255,0.72)" }}>
              Always on • Ask me anything
            </p>
          </div>

          {/* Right - controls */}
          <div className="flex items-center gap-1">
            {/* Expand / Collapse */}
            <HeaderBtn onClick={toggleExpand} title={chatExpanded ? "Collapse" : "Expand"}>
              {chatExpanded ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
            </HeaderBtn>
            {/* Minimize to pill */}
            <HeaderBtn onClick={toggleMinimize} title="Minimize">
              <Minus size={13} />
            </HeaderBtn>
            {/* Close */}
            <HeaderBtn onClick={closeChat} title="Close" danger>
              <X size={13} />
            </HeaderBtn>
          </div>
          
        </div>

        {/* Iframe chatbot */}
        <iframe
          src={iframeUrl}
          title="4AIQ Assistant"
          className="border-0 w-full"
          style={{ flex: 1, minHeight: 0 }}
        />

      </div>

      {/* Minimized strip */}
      {chatMinimized && (
        <div
          className="fixed flex items-center gap-2 px-3.5 py-2.5 select-none"
          style={{
            bottom: 24,
            right: 24,
            zIndex: 10000,
            borderRadius: 999,
            background: "linear-gradient(135deg, #0f766e 0%, #0d9488 60%, #14b8a6 100%)",
            boxShadow: "0 4px 20px rgba(13,148,136,0.45), 0 2px 8px rgba(0,0,0,0.12)",
            animation: "pillIn 0.2s cubic-bezier(.4,0,.2,1)",
          }}
        >
          {/* Icon + label */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white whitespace-nowrap pr-1">
              Aivi
            </span>
          </div>
 
          {/* Divider */}
          <div className="w-px h-4 mx-0.5" style={{ background: "rgba(255,255,255,0.25)" }} />
 
          {/* Restore */}
          <PillBtn onClick={toggleMinimize} title="Restore">
            <Maximize2 size={13} />
          </PillBtn>
 
          {/* Close */}
          <PillBtn onClick={closeChat} title="Close" danger>
            <X size={13} />
          </PillBtn>
        </div>
      )}

      <style>{`
        @keyframes floatIn {
          from { opacity: 0; transform: translateY(18px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes pillIn {
          from { opacity: 0; transform: translateY(10px) scale(0.92); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
};

// header control button
const HeaderBtn = ({ onClick, title, danger = false, children }) => (
  <button
    onClick={onClick}
    title={title}
    className="flex items-center justify-center w-7 h-7 rounded-lg"
    style={{ border: "none", background: "rgba(255,255,255,0.12)", color: "#fff", cursor: "pointer" }}
    onMouseEnter={e =>
      (e.currentTarget.style.background = danger ? "rgba(239,68,68,0.5)" : "rgba(255,255,255,0.24)")
    }
    onMouseLeave={e => (e.currentTarget.style.background = "rgba(255,255,255,0.12)")}
  >
    {children}
  </button>
);
 
// Pill button - inside minimized bubble 
const PillBtn = ({ onClick, title, danger = false, children }) => (
  <button
    onClick={onClick}
    title={title}
    className="flex items-center justify-center w-6 h-6 rounded-full"
    style={{ border: "none", background: "rgba(255,255,255,0.15)", color: "#fff", cursor: "pointer" }}
    onMouseEnter={e =>
      (e.currentTarget.style.background = danger ? "rgba(239,68,68,0.6)" : "rgba(255,255,255,0.28)")
    }
    onMouseLeave={e => (e.currentTarget.style.background = "rgba(255,255,255,0.15)")}
  >
    {children}
  </button>
);